import React from "react";
import { Box, Typography } from "@mui/material";
import tokens from "../../../shared/theme/tokens";
import lockup from "../../../assets/images/brand/tenantport-lockup.svg";
import { LanguageSwitcher } from "../../OtherUtilities/LanguageSwitcher/LanguageSwitcher";
import { LanguageModal } from "../../Modals/LanguageModal/LanguageModal";

/** localStorage flag — set once the first-visit language modal is answered. */
const LANGUAGE_PROMPTED_KEY = "tp_lang_prompted";

interface WizardShellProps {
  children: React.ReactNode;
  /** Optional stepper slot rendered directly below the top bar (not scrolled). */
  stepper?: React.ReactNode;
  /** Optional sticky footer (white, top border) pinned to the bottom. */
  footer?: React.ReactNode;
  /** Max width of the centered body column. */
  maxWidth?: number;
  /** Optional property label displayed in the wizard top bar. */
  propertyName?: string;
}

/**
 * Presentation-only step-flow shell (ui-v2) for unauthenticated wizards
 * (registration, lease application/payment, affordable application).
 *
 * A white brand top bar, an optional stepper slot, a scrollable body on
 * pageBg with a centered maxWidth column, and an optional sticky footer.
 * Holds no form, mutation, or navigation logic — callers pass those in.
 */
export const WizardShell = ({
  children,
  stepper,
  footer,
  maxWidth = 720,
  propertyName,
}: WizardShellProps) => {
  // First-visit language chooser — shown once per device. Spanish-speaking
  // applicants were missing the small header switcher in the ui-v2 theme.
  const [showLanguageModal, setShowLanguageModal] = React.useState(false);

  React.useEffect(() => {
    try {
      if (!localStorage.getItem(LANGUAGE_PROMPTED_KEY)) {
        setShowLanguageModal(true);
      }
    } catch {
      // localStorage unavailable (private mode / SSR) — skip the prompt.
    }
  }, []);

  const handleLanguageModalClose = () => {
    try {
      localStorage.setItem(LANGUAGE_PROMPTED_KEY, "1");
    } catch {
      // ignore — worst case the modal shows again next visit.
    }
    setShowLanguageModal(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "@supports not (height: 100dvh)": { height: "100vh" },
        backgroundColor: tokens.colors.pageBg,
        overflow: "hidden",
      }}
    >
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr) auto",
            sm: "minmax(120px, 1fr) minmax(0, 2fr) minmax(80px, 1fr)",
          },
          alignItems: "center",
          columnGap: 2,
          px: { xs: 2, sm: 3 },
          height: 60,
          backgroundColor: tokens.colors.surface,
          borderBottom: `1px solid ${tokens.colors.border}`,
        }}
      >
        <Box
          sx={{
            display: { xs: propertyName ? "none" : "flex", sm: "flex" },
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <img
            src={lockup}
            alt="TenantPort"
            style={{ height: 30, maxWidth: "100%" }}
          />
        </Box>

        {propertyName && (
          <Typography
            component="div"
            title={propertyName}
            sx={{
              gridColumn: { xs: 1, sm: 2 },
              justifySelf: { xs: "start", sm: "center" },
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: tokens.colors.primaryDeep,
              fontFamily: tokens.fonts.display,
              fontSize: { xs: 14, sm: 16 },
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {propertyName}
          </Typography>
        )}

        <Box sx={{ gridColumn: { xs: 2, sm: 3 }, justifySelf: "end" }}>
          {/* Every wizard flow's legacy AppBar carried a language switcher —
              Spanish-speaking applicants rely on it mid-flow. */}
          <LanguageSwitcher />
        </Box>
      </Box>

      {stepper && <Box sx={{ flexShrink: 0 }}>{stepper}</Box>}

      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box
          sx={{
            maxWidth: `${maxWidth}px`,
            mx: "auto",
            width: "100%",
            px: { xs: 2, sm: 3 },
            py: { xs: 3, sm: 4 },
          }}
        >
          {children}
        </Box>
      </Box>

      {footer && (
        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: tokens.colors.surface,
            borderTop: `1px solid ${tokens.colors.border}`,
          }}
        >
          <Box
            sx={{
              maxWidth: `${maxWidth}px`,
              mx: "auto",
              width: "100%",
              px: { xs: 2, sm: 3 },
              py: 2,
            }}
          >
            {footer}
          </Box>
        </Box>
      )}

      <LanguageModal
        open={showLanguageModal}
        onClose={handleLanguageModalClose}
      />
    </Box>
  );
};
